import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { FileType } from 'src/types/file';
import { ReceivingProductType, ReceivingType } from 'src/types/receiving';
import { checkPermission } from 'src/utils/checkPermission';
import isDeepEqual from 'src/utils/isDeepEqual';
import {
    receivingJoinedToFiles,
    RECEIVING_FILE_ATTACHMENT_TYPE,
    RECEIVING_FILE_DESCRIPTION_MAX_LENGTH,
} from 'src/utils/receivingFiles';
import { statusesTo } from './constants';

import {
    parseProductQuantityValue,
    ProductQuantityInputValue,
    shouldRestoreProductQuantityOnBlur,
} from 'src/utils/productQuantityInput';

export type ProductCountInputValue = ProductQuantityInputValue;

export {
    isEmptyProductQuantityInput as isEmptyProductCountInput,
    parseProductQuantityValue as parseProductCountValue,
    toProductQuantityInputValue as toProductCountInputValue,
} from 'src/utils/productQuantityInput';

export const changeReceivingProductQuantity = (
    products: ReceivingProductType[],
    receivingsProductsId: number,
    newQuantity: number
): ReceivingProductType[] => {
    if (newQuantity <= 0) {
        return products;
    }
    return products.map((product) => {
        if (product.RECEIVINGS_PRODUCTS_ID !== receivingsProductsId) {
            return product;
        }
        return { ...product, QUANTITY: newQuantity, LINE_STATE: 'E' };
    });
};

export const finalizeReceivingProductQuantityOnBlur = (
    products: ReceivingProductType[],
    receivingsProductsId: number,
    lastValue: ProductCountInputValue,
    quantityOnFocus: number
): ReceivingProductType[] => {
    return products.map((product) => {
        if (product.RECEIVINGS_PRODUCTS_ID !== receivingsProductsId) {
            return product;
        }
        if (shouldRestoreProductQuantityOnBlur(lastValue)) {
            return { ...product, QUANTITY: quantityOnFocus, LINE_STATE: 'E' };
        }
        return product;
    });
};

export const getAllowedStatuses = (
    receivingStatusCodes: ConstantsCodeType[],
    receivingStatusCodesObject: ConstantsCodesObjectType,
    receivingData?: { STATUS?: string } | null
): ConstantsCodeType[] => {
    if (checkPermission('RECEIVING.EDITALL')) {
        return receivingStatusCodes;
    }

    if (!receivingData || !receivingData.STATUS) {
        return [];
    }

    if (!checkPermission(`RECEIVING.STATUS.EDIT.${receivingData.STATUS.toUpperCase()}`)) {
        return [];
    }

    return [
        ...(statusesTo[receivingData && receivingData.STATUS] || [])
            .filter((statusKey) => {
                return checkPermission(`RECEIVING.STATUS.LIST.${statusKey.toUpperCase()}`);
            })
            .map((statusKey) => {
                return receivingStatusCodesObject[statusKey];
            })
            .filter((status) => status !== undefined), // Filtrujeme undefined hodnoty
    ];
};

export const normalizeReceivingFiles = (files?: FileType[]): FileType[] | undefined =>
    files?.map((f) => ({
        ...f,
        type: RECEIVING_FILE_ATTACHMENT_TYPE,
        description:
            f.description != null && f.description !== ''
                ? String(f.description).slice(0, RECEIVING_FILE_DESCRIPTION_MAX_LENGTH)
                : f.description,
    }));

/**
 * Partial PATCH payload for a form locked by status (user has only property rights, e.g. FILES.EDIT.<STATUS>).
 * Contains only the changed fields; FILES replace the whole set on the backend, so they are sent only when changed.
 */
export const buildLockedReceivingPatchPayload = ({
    editData,
    data,
    canEditFiles,
}: {
    editData: ReceivingType;
    data?: ReceivingType | null;
    canEditFiles: boolean;
}): Partial<ReceivingType> => {
    const payload: Partial<ReceivingType> = {};

    if (canEditFiles) {
        const files = normalizeReceivingFiles(editData.FILES) || [];
        const savedFiles = normalizeReceivingFiles(data?.FILES ?? receivingJoinedToFiles(data?.RECEIVING_FILES)) || [];
        if (!isDeepEqual(files, savedFiles)) {
            payload.FILES = files;
        }
    }

    if ((editData.ESHOP_NOTE || '') !== (data?.ESHOP_NOTE || '')) {
        payload.ESHOP_NOTE = editData.ESHOP_NOTE || '';
    }

    return payload;
};
